import { EventEmitter, Output, ChangeDetectionStrategy, signal } from '@angular/core';
import { AfterViewInit, Component, ElementRef, OnInit, OnDestroy, Input, ViewChild } from '@angular/core'
import { Observable, Subject } from 'rxjs'
import { NavigationEnd, NavigationStart, Router } from '@angular/router'

import { Store } from '@ngrx/store'
import * as fromSidebarStore from '@app/sidebar/store'
import * as fromShopStore from '@app/shop/store'

import { ScheduleEvent, ScheduleEventCursor } from '@app/sidebar/models/schedule-event.model'
import { map, takeUntil } from 'rxjs/operators'
import { FOLLOW_UP_RESOURCE } from '@app/shared/models/followUpTypes.modal'
import { OpenModal } from '@app/modals/store'
import { getContentHTMLWithMention, RedirectService } from '@app/shared'
import * as base64Util from "@app/shared/utils/base64.util";

@Component({
    selector: 'app-header-events',
    templateUrl: './events.component.html',
    styleUrls: ['./events.component.css'],
    animations: [],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class HeaderEventsComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input()
  type = 'notes'
  @Input()
  currentUser
  @Input()
  users = []
  @Output()
  onMobileClick = new EventEmitter<void>()
  @Input()
  options = {
    mobile: false
  }

  events$: Observable<ScheduleEvent[]>
  destroyed$ = new Subject<boolean>();
  isActive: true

  @ViewChild('scrollViewport')
  scrollViewport: ElementRef<HTMLElement>
  @ViewChild('scrollSentinel')
  scrollSentinel: ElementRef<HTMLElement>

  readonly pageSize = 20
  showDeleted = signal(false)
  loading = signal(false)
  hasMore = signal(false)
  // Keyset cursor of the last loaded page - survives events being deleted or arriving
  // between two pages, which an offset would not
  private nextCursor: ScheduleEventCursor | null = null
  private observer: IntersectionObserver

  locationStr = location.href;
  FOLLOW_UP_RESOURCE = FOLLOW_UP_RESOURCE

  constructor(
    private coreStore: Store<fromSidebarStore.State>,
    private shopStore: Store<fromShopStore.State>,
    private router: Router,
    private redirectService: RedirectService
  ) { }

  ngOnInit() {
    this.events$ = this.coreStore.select(fromSidebarStore.selectVisibleScheduleEvents).pipe(
      map(events=>{
        if(!events.length) return []
        events = events.map(e=>{
          return {...e,_contentHTML: getContentHTMLWithMention(e.content,[...(this.currentUser?.company?.contactGroups||[]), ...this.users], 'span', 'bg-blue-100 text-blue-900 rounded-lg px-2 py-0.5 text-sm')}
        })
        return events
      })
    )
    // .pipe(
    //   map(events => events.map(e => ({
    //     ...e,
    //     _routerLinkUrl: this.redirect(e.resource, e.resourceId)
    //   })))
    // )
    this.coreStore
      .select(fromSidebarStore.getScheduleEventLoading)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(loading => {
        this.loading.set(loading)
        // A page can come back empty once the resources the user may not see are filtered
        // out, so keep pulling until the viewport is covered or the feed is exhausted
        if (!loading) requestAnimationFrame(() => this.loadMoreIfSentinelVisible())
      })

    this.coreStore
      .select(fromSidebarStore.selectScheduleEventsHasMore)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(hasMore => this.hasMore.set(hasMore))

    this.coreStore
      .select(fromSidebarStore.selectScheduleEventsNextCursor)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(cursor => this.nextCursor = cursor)

    this.router.events.subscribe(event =>
      event instanceof NavigationStart || event instanceof NavigationEnd ? this.locationStr = event?.url : ''
    )

    this.load(false)
  }

  ngAfterViewInit() {
    if (!this.scrollSentinel || !this.scrollViewport) return

    this.observer = new IntersectionObserver(
      entries => entries.some(entry => entry.isIntersecting) && this.loadMore(),
      { root: this.scrollViewport.nativeElement, rootMargin: '200px' }
    )
    this.observer.observe(this.scrollSentinel.nativeElement)
  }

  toggleDeleted(showDeleted: boolean) {
    if (this.showDeleted() === showDeleted) return
    this.showDeleted.set(showDeleted)
    this.load(false)
  }

  loadMore() {
    // Without a cursor the request would restart the list, so a missing one ends the paging
    // instead of looping on the first page forever
    if (!this.hasMore() || this.loading() || !this.nextCursor) return
    this.load(true)
  }

  private loadMoreIfSentinelVisible() {
    if (!this.scrollSentinel || !this.scrollViewport) return
    if (!this.hasMore() || this.loading() || !this.nextCursor) return

    const viewport = this.scrollViewport.nativeElement.getBoundingClientRect()
    const sentinel = this.scrollSentinel.nativeElement.getBoundingClientRect()

    if (sentinel.top <= viewport.bottom + 200) this.loadMore()
  }

  private load(append: boolean) {
    const cursor = append ? this.nextCursor : null

    this.coreStore.dispatch(
      new fromSidebarStore.GetScheduleEvents({
        isProcessed: this.showDeleted(),
        limit: this.pageSize,
        cursorCreatedAt: cursor ? new Date(cursor.createdAt).toISOString() : undefined,
        cursorId: cursor ? cursor._id : undefined,
        append: append && !!cursor
      })
    )
  }

  deleteEvent(scheduleEvent) {
    this.coreStore.dispatch(
      new fromSidebarStore.DeleteScheduleEvent({ item: scheduleEvent })
    )
  }

  redirect(event: ScheduleEvent) {
    let resource = event.resource
    let resourceId = event.resourceId
    let extraResourceId = event.extraResourceId
    const queryParams = event?.params ? { queryParams: event.params } : {}
    let isLoadboard = window.location.href.indexOf('loadboard') > -1
    this.options.mobile ? this.onMobileClick.emit() : null
    let userIndex = this.redirectService.getUrlIndex()
    switch (resource) {
      case FOLLOW_UP_RESOURCE.CLAIM:
        return this.router.navigate([`/${userIndex}/claims/${resourceId}`])
      case FOLLOW_UP_RESOURCE.ORDER:
        return this.router.navigate(isLoadboard ? [`/${userIndex}/loadboard/${resourceId}`] : [`/${userIndex}/quotes/${resourceId}`])
      case FOLLOW_UP_RESOURCE.ORDER_BILLING:
        return this.router.navigate(isLoadboard ? [`/${userIndex}/loadboard/${resourceId}`] : [`/${userIndex}/quotes/${resourceId}`])
      case FOLLOW_UP_RESOURCE.ORDER_BROKER:
        return this.router.navigate(isLoadboard ? [`/${userIndex}/loadboard/${resourceId}`] : [`/${userIndex}/quotes/${resourceId}`])
      case FOLLOW_UP_RESOURCE.ORDER_COLLECTION:
        return this.router.navigate(isLoadboard ? [`/${userIndex}/loadboard`] : [`/${userIndex}/quotes`], queryParams)
      case FOLLOW_UP_RESOURCE.ORDER_FILTERS:
        return this.router.navigate(isLoadboard ? [`/${userIndex}/loadboard`] : [`/${userIndex}/quotes`], queryParams)
      case FOLLOW_UP_RESOURCE.ACCOUNT_CONTACT:
        return this.router.navigate([`/${userIndex}/crm/accounts/${resourceId}`])
      case FOLLOW_UP_RESOURCE.CONTACT_NOTE:
        return this.coreStore.dispatch(
          new OpenModal({
            type: 'UPDATE_CONTACT',
            props: { contactId: resourceId, size: 'md', windowClass: "contact-modal" }
          })
        )
      case FOLLOW_UP_RESOURCE.EQUIPMENT:
        return this.coreStore.dispatch(
          new OpenModal({
            type: 'EQUIPMENT_FORM',
            props: { _id: resourceId, carrierId: this.currentUser.company._id, tab: 'Notes', windowClass : "equipment-form-modal" },
          })
        )
      case FOLLOW_UP_RESOURCE.ORDER_ACCOUNTING:
        return this.router.navigate(isLoadboard ? [`/${userIndex}/loadboard/${resourceId}/accounting`] : [`/${userIndex}/quotes/${resourceId}/accounting`])
      case FOLLOW_UP_RESOURCE.SHOP_ORDER:
        return this.router.navigate([`/${userIndex}/shop/${resourceId}`])
      case FOLLOW_UP_RESOURCE.SHOP_SERVICE:
        this.router.navigate([`/${userIndex}/shop/${event?._orderId}`])
        return this.coreStore.dispatch(
          new OpenModal({
            type: 'SHOP_SERVICE',
            props: { _id: resourceId, _orderId: event?._orderId }
          }))
      case FOLLOW_UP_RESOURCE.CANDIDATE:
        return this.router.navigate([`/${userIndex}/recruiter/${resourceId}`])
      case FOLLOW_UP_RESOURCE.EXPENSE:
        return this.coreStore.dispatch(
          new OpenModal({
            type: 'EXPENSE',
            props: { _id: resourceId }
          })
        )

      case FOLLOW_UP_RESOURCE.TRIP:
        return this.router.navigate([`/${userIndex}/trips/${resourceId}/stops`])
      case FOLLOW_UP_RESOURCE.BOOK_NOW:
        let hash:string;
        if(event._carrierId === this.currentUser.company._id){
          hash = base64Util.encodeUrlSafe(
            JSON.stringify({
              orderId: resourceId,
              carrierId: event._carrierId
            })
          );
        }else{
          hash = base64Util.encodeUrlSafe(
            JSON.stringify({
              orderId: resourceId
            })
          );
        }

        return this.router.navigate([`/${userIndex}/v/loadboard/orders/${hash}`])
      case FOLLOW_UP_RESOURCE.INVOICE:
        return this.router.navigate([`/${userIndex}/${isLoadboard ? 'loadboard' : 'quotes'}/${extraResourceId}/accounting`])
      case FOLLOW_UP_RESOURCE.COMPANY: 
        return this.router.navigate([`/${userIndex}/crm/accounts/${resourceId}`])
      default:
        return this.router.navigate([`/${userIndex}/${resource}/${resourceId}`])
    }
  }

  markAsRead(item) {
    if (!item.isRead) {
      this.coreStore.dispatch(
        new fromSidebarStore.MarkScheduleEventAsRead({ items: [item] })
      )
    }
  }

  markAsUnread(item) {
    if (item.isRead) {
      this.coreStore.dispatch(
        new fromSidebarStore.MarkScheduleEventAsUnread({ items: [item] })
      )
    }
  }

  ngOnDestroy() {
    this.observer?.disconnect()
    this.destroyed$.next(true)
    this.destroyed$.complete()
  }
}
